import { saveAs } from 'file-saver';
import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { Card, Col, Input, Row } from 'reactstrap';
import './Wishlist.scss';
import { DateRangePicker, Drawer } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import { fetchWishlistItems, updateWishlistCsStatus, fetchWishlistCommentLog, exportWishlistItems } from '../actions';
import { ErrorToast } from '../../../utils/Toaster';
import DHPagination from '../../../components/Pagination';
import FilterSearchQueue from '../../../components/FilterSearch/FilterSearchQueue';
import { Spinner } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const WishlistList = forwardRef(({ country = 'ae', limit = 10, customerId, sku }, ref) => {
    const [items, setItems] = useState([]);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [editCsContacted, setEditCsContacted] = useState('');
    const [editComments, setEditComments] = useState('');
    const [editConvertStatus, setEditConvertStatus] = useState(''); // Add state for convertStatus
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [pageLimit, setPageLimit] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [searchFilter, setSearchFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [advancedFilter, setAdvancedFilter] = useState({});
    const [commentModal, setCommentModal] = useState({ open: false, comment: '', updatedBy: '' });
    const [commentLoading, setCommentLoading] = useState(false);
    const location = useLocation();
    const search = useLocation().search;
    const paramsCountry = new URLSearchParams(search).get('cn');
    let currentCountry = localStorage.getItem('WEBSITE_COUNTRY');
    let cntry = currentCountry;
    const [userID, setUserID] = useState('');
    const [userName, setUserName] = useState('');

    // fetchData needs to be accessible in modal form as well
    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const start = page * pageLimit;
            const response = await fetchWishlistItems({
                country: paramsCountry ? paramsCountry : cntry,
                limit: pageLimit,
                start,
                customerId,
                sku,
                search: searchFilter,
                status: statusFilter,
                ...advancedFilter,
            });
            // Defensive: ensure items is always an array
            const itemsArray = Array.isArray(response) ? response : (Array.isArray(response?.data) ? response.data : []);
            setItems(itemsArray);
            setTotalCount(itemsArray.length < pageLimit ? start + itemsArray.length : (page + 2) * pageLimit); // fallback if API doesn't return total
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchData();
    }, [country, pageLimit, page, customerId, sku, searchFilter, statusFilter, advancedFilter]);
    useEffect(() => {
        if (localStorage.getItem('authUser')) {
            const userObj = JSON.parse(localStorage.getItem('authUser'));
            if (userObj?.role_id !== undefined) {
                setUserID(userObj?.user_id);
                setUserName(userObj?.user_name);
            }
        }
    }, []);

    // Export date range state for DateRangePicker
    const [dateRangeValue, setDateRangeValue] = useState(null); // [startDate, endDate]
    const [exportRange, setExportRange] = useState({ from: '', to: '' });
    const disableFutureDates = date => date > new Date();
    const handleDateChange = range => {
        setDateRangeValue(range);
        if (range && range.length === 2) {
            setExportRange({
                from: range[0] ? range[0].toISOString().slice(0, 10) : '',
                to: range[1] ? range[1].toISOString().slice(0, 10) : ''
            });
        } else {
            setExportRange({ from: '', to: '' });
        }
    };
    const handleDateClose = () => {
        setDateRangeValue(null);
        setExportRange({ from: '', to: '' });
    };
    const handleExport = async (exportRange) => {
        if (!exportRange.from || !exportRange.to) {
            ErrorToast('Please select a date range before exporting.');
            return;
        }
        // Check if date range is more than 90 days
        const fromDate = new Date(exportRange.from);
        const toDate = new Date(exportRange.to);
        const diffTime = Math.abs(toDate - fromDate);
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        if (diffDays > 90) {
            ErrorToast('Date range cannot exceed 90 days.');
            return;
        }
        const formatDate = d => {
            if (!d) return '';
            if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
            const dateObj = new Date(d);
            if (isNaN(dateObj)) return '';
            const pad = n => n < 10 ? '0' + n : n;
            return dateObj.getFullYear() + '-' + pad(dateObj.getMonth() + 1) + '-' + pad(dateObj.getDate());
        };
        try {
            const blob = await exportWishlistItems({
                frmDt: formatDate(exportRange.from),
                toDt: formatDate(exportRange.to),
                country: paramsCountry ? paramsCountry : cntry
            });
            // If the response is a JSON error, handle it here (defensive, in case API returns JSON as blob)
            if (blob && blob.type && blob.type.indexOf('application/json') !== -1) {
                const text = await blob.text();
                let json;
                try {
                    json = JSON.parse(text);
                } catch (e) {
                    ErrorToast('Export failed: Invalid server response.');
                    return;
                }
                ErrorToast(json.message || 'Export failed: Unknown error.');
                return;
            }
            saveAs(blob, 'wishlist-export.xlsx');
        } catch (err) {
            ErrorToast('Export failed: ' + (err?.message || 'Unknown error'));
        }
    };

    useImperativeHandle(ref, () => ({
        handleExport
    }));
    return (
        <div className="table-responsive dt-table">
            <FilterSearchQueue
                type="cswishlist"
                setAdvancedFilter={setAdvancedFilter}
                setSearchFilter={setSearchFilter}
                setStatusFilter={setStatusFilter}
                onSearchButtonClick={() => setPage(0)}
            />
            {loading ? (
                <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 120 }}>
                    <Spinner className="ms-2 sploader" color="primary" />
                </div>
            ) : error ? (
                <div className="text-danger">{error}</div>
            ) : items.length === 0 ? (
                <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 120 }}>
                    No wishlist items found.
                </div>
            ) : (
                <>
                    <table className="table table-bordered text-center">
                        <thead>
                            <tr>
                                <th className="text-center">SKU</th>
                                <th className="text-center">Added At</th>
                                <th style={{ textAlign: 'left', paddingLeft: '20px' }}>Email</th>
                                <th className="text-center">Mobile</th>
                                <th className="text-center">Country</th>
                                <th className="text-center">CS Contacted</th>
                                <th className="text-center">Contacted By</th>
                                <th className="text-center">Status</th>
                                <th className="text-center">Action</th>
                            </tr>
                        </thead>
                        {editLoading || loading ? (
                            <>
                                <Spinner className="ms-2 sploader" color="primary" />
                                <tbody>
                                    {[...Array(2)].map((_, index) => (
                                        <tr key={index}>
                                            <td>
                                                <p className="placeholder-glow">
                                                    <span className="placeholder col-2"></span>
                                                </p>
                                            </td>
                                            <td>
                                                <p className="placeholder-glow">
                                                    <span className="placeholder col-12"></span>
                                                    <span className="placeholder col-6"></span>
                                                    <span className="placeholder" style={{ width: '25%' }}></span>
                                                    <span className="placeholder w-75"></span>
                                                </p>
                                            </td>
                                            <td>
                                                <p className="placeholder-glow d-flex flex-column gap-1">
                                                    <span className="placeholder col-6"></span>
                                                </p>
                                            </td>
                                            <td>
                                                <p className="placeholder-glow">
                                                    <span className="placeholder col-6"></span>
                                                </p>
                                            </td>
                                            <td>
                                                <p className="placeholder-glow">
                                                    <span className="placeholder col-6"></span>
                                                </p>
                                            </td>
                                            <td>
                                                <p className="placeholder-glow">
                                                    <span className="placeholder col-6"></span>
                                                </p>
                                            </td>
                                            <td>
                                                <p className="placeholder-glow">
                                                    <span className="placeholder col-6"></span>
                                                </p>
                                            </td>
                                            <td>
                                                <p className="placeholder-glow">
                                                    <span className="placeholder col-6"></span>
                                                    <span className="placeholder" style={{ width: '25%' }}></span>
                                                </p>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </>
                        ) : (
                            <tbody>
                                {Array.isArray(items) && items.map((item, idx) => {
                                    const dateObj = new Date(item.createdAt);
                                    const pad = n => n < 10 ? '0' + n : n;
                                    const formattedDate = item.createdAt ? (
                                        pad(dateObj.getDate()) + '-' +
                                        pad(dateObj.getMonth() + 1) + '-' +
                                        dateObj.getFullYear().toString().slice(-2) + ' ' +
                                        pad(dateObj.getHours()) + ':' +
                                        pad(dateObj.getMinutes()) + ':' +
                                        pad(dateObj.getSeconds())
                                    ) : '-';
                                    // Status logic
                                    let statusLabel = 'Not available';
                                    let statusClass = 'badge bg-warning text-dark';
                                    if (item.convertStatus === 1) {
                                        statusLabel = 'Sale';
                                        statusClass = 'badge bg-success';
                                    } else if (item.convertStatus === 2) {
                                        statusLabel = 'Not interested';
                                        statusClass = 'badge bg-danger';
                                    } else if (item.convertStatus === 3) {
                                        statusLabel = 'No Answer';
                                        statusClass = 'badge bg-danger';
                                    } else if (item.convertStatus === 4) {
                                        statusLabel = 'In Progress';
                                        statusClass = 'badge bg-primary';
                                    } else if (item.convertStatus === undefined || item.convertStatus === null) {
                                        statusLabel = 'Not available';
                                        statusClass = 'badge bg-warning text-dark';
                                    }
                                    return (
                                        <tr key={item._id || idx}>
                                            <td className="text-center">
                                                {item.sku ? (
                                                    <>
                                                        <a href={`https://www.danubehome.com/ae/en/p/from-oms-preview-${item.sku}`} target="_blank" rel="noopener noreferrer">
                                                            {item.sku}
                                                        </a>
                                                        <a href={`https://www.danubehome.com/ae/en/p/from-oms-preview-${item.sku}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8 }} title="Go to Product">
                                                            <i className="mdi mdi-arrow-top-right-thick" style={{ fontSize: 18, verticalAlign: 'middle' }}></i>
                                                        </a>
                                                    </>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                            <td className="text-center">{formattedDate}</td>
                                            <td style={{ textAlign: 'left', paddingLeft: '20px' }}>{item.email || '-'}</td>
                                            <td className="text-center">{item.mobile || '-'}</td>
                                            <td className="text-center">{item.country || '-'}</td>
                                            <td className="text-center">
                                                {item.isCsContacted ? (
                                                    <span className="bg-soft-success text-success badge badge-secondary">Yes</span>
                                                ) : (
                                                    <span className="bg-soft-danger text-danger badge badge-secondary">No</span>
                                                )}
                                            </td>
                                            <td className="text-center">{item.csAgent || '-'}</td>
                                            <td className="text-center">
                                                <span className={statusClass}>{statusLabel}</span>
                                            </td>
                                            <td className="text-center">
                                                <a
                                                    type="button"
                                                    className="btn btn-sm btn-outline-warning waves-effect waves-light"
                                                    href="#"
                                                    onClick={e => {
                                                        if (item.convertStatus === 2 || item.convertStatus === true) return;
                                                        e.preventDefault();
                                                        setEditItem(item);
                                                        setEditCsContacted(item.isCsContacted ? 'yes' : 'no');
                                                        setEditComments(item.comments || '');
                                                        setEditConvertStatus(item.convertStatus ? String(item.convertStatus) : ''); // Set as string for select
                                                        setEditModalOpen(true);
                                                    }}
                                                    disabled={item.isCsContacted === 1 || item.isCsContacted === true}
                                                >
                                                    <i className="mdi mdi-border-color"></i>
                                                </a>
                                                <a
                                                    type="button"
                                                    className="btn btn-sm btn-outline-info waves-effect waves-light ms-2"
                                                    href="#"
                                                    title="View Comment"
                                                    onClick={async e => {
                                                        e.preventDefault();
                                                        setCommentLoading(true);
                                                        try {
                                                            const response = await fetchWishlistCommentLog({
                                                                event_id: item.wishlistItemId
                                                            });
                                                            // Filter out records with empty comment
                                                            const filtered = Array.isArray(response?.data)
                                                                ? response.data.filter(row => row.comment && row.comment.trim() !== '')
                                                                : [];
                                                            setCommentModal({
                                                                open: true,
                                                                data: filtered
                                                            });
                                                        } catch (err) {
                                                            setCommentModal({
                                                                open: true,
                                                                data: []
                                                            });
                                                        } finally {
                                                            setCommentLoading(false);
                                                        }
                                                    }}
                                                >
                                                    <i className="mdi mdi-comment-text-outline"></i>
                                                </a>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        )}
                    </table>
                    <DHPagination
                        totalPages={Math.ceil(totalCount / pageLimit)}
                        currentPage={page}
                        setPaginationValue={({ page }) => setPage(page)}
                        setLimitValue={({ limit }) => setPageLimit(limit)}
                    />
                    {editModalOpen && (
                        <Drawer
                            placement="right"
                            open={editModalOpen}
                            onClose={() => setEditModalOpen(false)}
                            size="sm"
                            backdrop
                        >
                            <Card className="card-h-100 OrderDetailsBox drawer-content-card">
                                <div className="drawer-header-row">
                                    <h5 className="mb-0">Edit CS Contacted</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setEditModalOpen(false)}
                                        aria-label="Close"
                                    ></button>
                                </div>
                                <div className="drawer-body-content">
                                    <form
                                        onSubmit={async (e) => {
                                            e.preventDefault();
                                            // Comments must be mandatory
                                            if (!editComments || editComments.trim() === '') {
                                                setEditError('Comments are required.');
                                                return;
                                            }
                                            setEditLoading(true);
                                            setEditError(null);
                                            try {
                                                await updateWishlistCsStatus({
                                                    wishlistId: editItem.wishlistId,
                                                    wishlistItemId: editItem.wishlistItemId,
                                                    isCsContacted: editCsContacted === 'yes',
                                                    comments: editComments,
                                                    user_id: userID,
                                                    user_name: userName,
                                                    convertStatus: editConvertStatus ? Number(editConvertStatus) : 0,
                                                    csUpdatedAt: new Date().toISOString(),
                                                    csAgent: userName
                                                });
                                                setEditModalOpen(false);
                                                setEditItem(null);
                                                setEditComments('');
                                                setEditCsContacted('');
                                                setEditConvertStatus('');
                                                // Optionally refresh list
                                                fetchData();
                                            } catch (err) {
                                                setEditError(err.message);
                                            } finally {
                                                setEditLoading(false);
                                            }
                                        }}
                                    >
                                        <Row>
                                            <Col md={6}>
                                                <div className="mb-3">
                                                    <label className="form-label font-size-13 text-muted">Contacted</label>
                                                    <select
                                                        className="form-control"
                                                        value={editCsContacted}
                                                        onChange={e => setEditCsContacted(e.target.value)}
                                                    >
                                                        <option value="yes">Yes</option>
                                                        <option value="no">No</option>
                                                    </select>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="mb-3">
                                                    <label className="form-label font-size-13 text-muted">Status</label>
                                                    <select
                                                        className="form-control"
                                                        value={editConvertStatus}
                                                        onChange={e => setEditConvertStatus(e.target.value)}
                                                    >
                                                        <option value="">Select Status</option>
                                                        <option value="4">In progress</option>
                                                        <option value="1">Sale</option>
                                                        <option value="2">Not interested</option>
                                                        <option value="3">No Answer</option>
                                                    </select>
                                                </div>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={12}>
                                                <div className="mb-3">
                                                    <label className="form-label font-size-13 text-muted">Comments</label>
                                                    <textarea
                                                        className="form-control"
                                                        value={editComments}
                                                        onChange={e => setEditComments(e.target.value)}
                                                        required
                                                    />
                                                </div>
                                            </Col>
                                        </Row>
                                        {editError && <div className="text-danger">{editError}</div>}
                                        <div className="mt-3">
                                            <button type="submit" className="btn btn-primary" disabled={editLoading}>Save</button>
                                        </div>
                                    </form>
                                </div>
                            </Card>
                        </Drawer>
                    )}
                    {commentModal.open && (
                        <Drawer
                            placement="right"
                            open={commentModal.open}
                            onClose={() => setCommentModal({ ...commentModal, open: false })}
                            size="sm"
                            backdrop
                        >
                            <Card className="card-h-100 OrderDetailsBox drawer-content-card drawer-width-narrow">
                                <div className="drawer-header-row">
                                    <h5 className="mb-0">Comment Details</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setCommentModal({ ...commentModal, open: false })}
                                        aria-label="Close"
                                    ></button>
                                </div>
                                <div className="drawer-body-content">
                                    {commentLoading ? (
                                        <Spinner className="ms-2 sploader" color="primary" />
                                    ) : (
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Comment</th>
                                                    <th>Update At</th>
                                                    <th>Updated By</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Array.isArray(commentModal.data) && commentModal.data.length > 0 ? (
                                                    commentModal.data.map((row, idx) => (
                                                        <tr key={row.id || idx}>
                                                            <td>{row.comment}</td>
                                                            <td>{row.created_at || '-'}</td>
                                                            <td>{row.user_name || '-'}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={3}>No comment records found.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    )}

                                </div>
                            </Card>
                        </Drawer>
                    )}
                </>
            )}
        </div>
    );
});

export default WishlistList;

